import { useContext, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  FeedDensityContext,
  isDenseFeed,
} from '@/features/action-cards-view/feed/feedDensity';
import { cn } from '@/lib/utils';

import type { FeedGroup } from '../feed-groups';

type FeedGroupHeadingProps = {
  group: FeedGroup;
  icon?: ReactNode;
  onToggle: (id: string) => void;
};

const iconSlot = (icon: ReactNode, compact: boolean) =>
  icon && compact ? (
    <span className="mr-[calc(12rem/14_-_0.5rem)] flex w-[calc(24rem/14)] shrink-0 justify-center">
      {icon}
    </span>
  ) : (
    icon
  );

// A section's heading folds the section; its count shows only while it is folded, since the open section shows
// its cards, and screen readers hear it either way. A group inside a section, such as an app's, is a quiet label
// above its cards
export const FeedGroupHeading = ({
  group,
  icon,
  onToggle,
}: FeedGroupHeadingProps) => {
  const compact = isDenseFeed(useContext(FeedDensityContext));

  if (group.level === 1)
    return (
      <div
        role="heading"
        aria-level={3}
        aria-label={group.label}
        className="flex h-7 w-full items-center gap-2 text-[13px] text-foreground-muted"
      >
        {iconSlot(icon, compact)}
        <span className="truncate">{group.label}</span>
      </div>
    );

  const canToggle = group.count > 0;
  const collapsed = group.collapsed && canToggle;

  return (
    <button
      type="button"
      aria-expanded={!collapsed}
      disabled={!canToggle}
      className="focus-ring group/heading flex h-9 w-full items-center gap-2 rounded-md text-left text-sm font-medium text-foreground-subtle transition-colors enabled:hover:text-foreground"
      onClick={() => onToggle(group.id)}
    >
      {iconSlot(icon, compact)}
      <span className="truncate">{group.label}</span>
      {/* Screen readers hear the count either way */}
      <span
        className={cn(
          'font-normal tabular-nums text-foreground-muted',
          !collapsed && 'sr-only'
        )}
      >
        {group.count}
      </span>
      {canToggle ? (
        <Icons.chevronDown
          aria-hidden
          className={cn(
            'size-3.5 shrink-0 text-foreground-muted transition-[transform,opacity]',
            // A collapsed section keeps its turned chevron, the only sign it is folded
            collapsed
              ? '-rotate-90'
              : 'opacity-0 group-hover/heading:opacity-100 group-focus-visible/heading:opacity-100'
          )}
        />
      ) : null}
    </button>
  );
};
